import React, { useEffect, useMemo, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Mic, Sparkles, ChefHat, ArrowDownToLine, Bed, Bath, Shirt, BedDouble, Sofa, ChevronLeft, ChevronRight, X, Camera } from 'lucide-react';
import { MobileOptionsSheet } from './MobileOptionsSheet';
import { interiorMenuData, InteriorMenuItem } from '../../types/interiorConfig';
const rooms = [{
  id: 'kitchen',
  name: 'Kitchen',
  floor: 'Main Floor',
  icon: ChefHat,
  planPosition: {
    top: '35%',
    left: '30%'
  }
}, {
  id: 'basement',
  name: 'Basement',
  floor: 'Basement',
  icon: ArrowDownToLine,
  planPosition: {
    top: '50%',
    left: '50%'
  }
}, {
  id: 'primary-bedroom',
  name: 'Primary Bedroom',
  floor: 'Upper Floor',
  icon: Bed,
  planPosition: {
    top: '25%',
    left: '70%'
  }
}, {
  id: 'ensuite',
  name: 'Ensuite',
  floor: 'Upper Floor',
  icon: Bath,
  planPosition: {
    top: '40%',
    left: '75%'
  }
}, {
  id: 'laundry',
  name: 'Laundry',
  floor: 'Main Floor',
  icon: Shirt,
  planPosition: {
    top: '70%',
    left: '25%'
  }
}, {
  id: 'bedroom-2',
  name: 'Bedroom 2',
  floor: 'Upper Floor',
  icon: BedDouble,
  planPosition: {
    top: '60%',
    left: '65%'
  }
}, {
  id: 'bedroom-3',
  name: 'Bedroom 3',
  floor: 'Upper Floor',
  icon: BedDouble,
  planPosition: {
    top: '75%',
    left: '70%'
  }
}, {
  id: 'living-room',
  name: 'Living Room',
  floor: 'Main Floor',
  icon: Sofa,
  planPosition: {
    top: '55%',
    left: '45%'
  }
}];
export function OverviewInteriorViewer() {
  const [activeRoomIndex, setActiveRoomIndex] = useState(0);
  const [activeItem, setActiveItem] = useState('Countertop');
  const [isAssistantActive, setIsAssistantActive] = useState(false);
  const [sheetState, setSheetState] = useState<'collapsed' | 'peek' | 'expanded'>('peek');
  const [activeCameraIndex, setActiveCameraIndex] = useState(1);
  const [showCameraMenu, setShowCameraMenu] = useState(false);
  const [showTeleportMenu, setShowTeleportMenu] = useState(false);
  const [slideDirection, setSlideDirection] = useState(1);
  const flatSteps = useMemo(() => {
    const steps: {
      item: InteriorMenuItem;
      category: string;
    }[] = [];
    const traverse = (items: InteriorMenuItem[], parentCategory: string) => {
      items.forEach(item => {
        if (item.type === 'item' && (item.options || !item.children)) {
          steps.push({
            item,
            category: parentCategory
          });
        }
        if (item.children) {
          traverse(item.children, item.type === 'category' ? item.name : parentCategory);
        }
      });
    };
    traverse(interiorMenuData, 'General');
    return steps;
  }, []);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const currentStep = flatSteps[currentStepIndex];
  const handleNextStep = () => {
    if (currentStepIndex < flatSteps.length - 1) {
      const nextIndex = currentStepIndex + 1;
      setCurrentStepIndex(nextIndex);
      setActiveItem(flatSteps[nextIndex].item.name);
    }
  };
  const handlePrevStep = () => {
    if (currentStepIndex > 0) {
      const prevIndex = currentStepIndex - 1;
      setCurrentStepIndex(prevIndex);
      setActiveItem(flatSteps[prevIndex].item.name);
    }
  };
  const handleActiveItemChange = (itemName: string) => {
    setActiveItem(itemName);
    const stepIdx = flatSteps.findIndex(s => s.item.name === itemName);
    if (stepIdx >= 0) {
      setCurrentStepIndex(stepIdx);
    }
  };
  const handleNextRoom = () => {
    setSlideDirection(1);
    setActiveRoomIndex(prev => (prev + 1) % rooms.length);
  };
  const handlePrevRoom = () => {
    setSlideDirection(-1);
    setActiveRoomIndex(prev => (prev - 1 + rooms.length) % rooms.length);
  };
  const handleSelectRoom = (index: number) => {
    setSlideDirection(index > activeRoomIndex ? 1 : -1);
    setActiveRoomIndex(index);
    setShowTeleportMenu(false);
  };
  const currentRoom = rooms[activeRoomIndex];
  const pressTimer = useRef<NodeJS.Timeout | null>(null);
  const handlePointerDown = (action: 'next' | 'prev') => {
    pressTimer.current = setTimeout(() => {
      setShowTeleportMenu(true);
      pressTimer.current = null;
    }, 500);
  };
  const handlePointerUp = (action: 'next' | 'prev') => {
    if (pressTimer.current) {
      clearTimeout(pressTimer.current);
      pressTimer.current = null;
      if (action === 'next') handleNextRoom();else handlePrevRoom();
    }
  };
  const handlePointerLeave = () => {
    if (pressTimer.current) {
      clearTimeout(pressTimer.current);
      pressTimer.current = null;
    }
  };
  useEffect(() => {
    return () => {
      if (pressTimer.current) clearTimeout(pressTimer.current);
    };
  }, []);
  return <div className="relative w-full h-full bg-black font-sans overflow-hidden" data-id="element-2044">
      {/* 3D Viewport */}
      <div className="absolute inset-0" data-id="element-2045">
        <img src="https://images.unsplash.com/photo-1600210492486-724fe5c67fb0?auto=format&fit=crop&w=1920&q=80" alt="Interior View" className="w-full h-full object-cover" data-id="element-2046" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-transparent to-black/30 pointer-events-none" data-id="element-2047" />
      </div>

      {/* Top Bar — Room Switcher */}
      <div className="absolute top-0 left-0 right-0 z-40 pt-2 md:pt-14 px-4 flex justify-center" data-id="element-2048">
        <div className="relative w-full flex items-center justify-between h-14 bg-black/40 backdrop-blur-xl rounded-full border border-white/10 shadow-lg px-1.5" data-id="element-2049">
          <button onPointerDown={() => handlePointerDown('prev')} onPointerUp={() => handlePointerUp('prev')} onPointerLeave={handlePointerLeave} className="relative z-10 w-11 h-11 rounded-full bg-white/10 flex items-center justify-center text-white/80 active:scale-95 transition-transform shrink-0" data-id="element-2050">
            <ChevronLeft className="w-6 h-6" data-id="element-2051" />
          </button>

          <div className="flex-1 relative h-full flex items-center justify-center overflow-hidden mx-2 pointer-events-none" data-id="element-2052">
            <AnimatePresence mode="popLayout" custom={slideDirection} data-id="element-2053">
              <motion.div key={currentRoom.id} custom={slideDirection} initial={{
              opacity: 0,
              x: slideDirection * 40
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={{
              opacity: 0,
              x: slideDirection * -40
            }} transition={{
              type: 'spring',
              damping: 25,
              stiffness: 300
            }} className="text-white font-bold tracking-wide text-center whitespace-nowrap absolute inset-0 flex items-center justify-center text-lg" data-id="element-2054">
                {currentRoom.name}
              </motion.div>
            </AnimatePresence>
          </div>

          <button onPointerDown={() => handlePointerDown('next')} onPointerUp={() => handlePointerUp('next')} onPointerLeave={handlePointerLeave} className="relative z-10 w-11 h-11 rounded-full bg-white/10 flex items-center justify-center text-white/80 active:scale-95 transition-transform shrink-0" data-id="element-2055">
            <ChevronRight className="w-6 h-6" data-id="element-2056" />
          </button>
        </div>
      </div>

      {/* Teleport Menu Portal */}
      {createPortal(<AnimatePresence data-id="element-2057">
          {showTeleportMenu && <>
              <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="fixed inset-0 z-[200] bg-black/60 backdrop-blur-sm" onClick={() => setShowTeleportMenu(false)} data-id="element-2058" />
              <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} className="fixed z-[201] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[90%] max-w-sm bg-slate-900 border border-white/10 rounded-2xl shadow-2xl overflow-hidden" data-id="element-2059">
                <div className="flex items-center justify-between p-4 border-b border-white/10" data-id="element-2060">
                  <h3 className="text-white font-semibold" data-id="element-2061">Teleport to Room</h3>
                  <button onClick={() => setShowTeleportMenu(false)} className="p-1 rounded-full bg-white/10 text-white/60 hover:text-white" data-id="element-2062">
                    <X className="w-4 h-4" data-id="element-2063" />
                  </button>
                </div>
                <div className="p-2 max-h-[50vh] overflow-y-auto custom-scrollbar" data-id="element-2064">
                  {rooms.map((room, idx) => {
              const Icon = room.icon;
              const isActive = activeRoomIndex === idx;
              return <button key={room.id} onClick={() => handleSelectRoom(idx)} className={`w-full flex items-center gap-3 px-3 py-3 rounded-xl transition-colors ${isActive ? 'bg-white/10' : 'active:bg-white/5'}`} data-id="element-2065">
                        <div className={`w-9 h-9 rounded-full flex items-center justify-center ${isActive ? 'bg-white text-black' : 'bg-white/10 text-white/60'}`} data-id="element-2066">
                          <Icon className="w-4 h-4" data-id="element-2067" />
                        </div>
                        <div className="flex-1 text-left" data-id="element-2068">
                          <span className={`text-sm font-medium block ${isActive ? 'text-white' : 'text-white/80'}`} data-id="element-2069">
                            {room.name}
                          </span>
                          <span className="text-xs text-white/40" data-id="element-2070">
                            {room.floor}
                          </span>
                        </div>
                      </button>;
            })}
                </div>
              </motion.div>
            </>}
        </AnimatePresence>, document.body)}

      {/* Floating Camera Button */}
      <motion.div className="absolute right-4 z-30" animate={{
      bottom: sheetState === 'collapsed' ? 136 : sheetState === 'peek' ? 302 : 92
    }} transition={{
      type: 'spring',
      damping: 25,
      stiffness: 200
    }} data-id="element-2071">
        <AnimatePresence data-id="element-2072">
          {showCameraMenu && <motion.div initial={{
          opacity: 0,
          x: 10,
          scale: 0.9
        }} animate={{
          opacity: 1,
          x: 0,
          scale: 1
        }} exit={{
          opacity: 0,
          x: 10,
          scale: 0.9
        }} className="absolute right-[60px] top-1/2 -translate-y-1/2 bg-black/60 backdrop-blur-xl border border-white/10 rounded-2xl p-1.5 flex gap-1 shadow-2xl" data-id="element-2073">
              {[1, 2, 3, 4].map(cam => <button key={cam} onClick={() => {
            setActiveCameraIndex(cam);
            setShowCameraMenu(false);
          }} className={`w-10 h-10 rounded-xl text-sm font-bold transition-all flex items-center justify-center ${activeCameraIndex === cam ? 'bg-white text-black shadow-sm' : 'text-white/60 hover:text-white hover:bg-white/10'}`} data-id="element-2074">
                  {cam}
                </button>)}
            </motion.div>}
        </AnimatePresence>

        <motion.button whileTap={{
        scale: 0.92
      }} onClick={() => setShowCameraMenu(!showCameraMenu)} className={`w-14 h-14 rounded-full flex items-center justify-center shadow-2xl border-2 transition-colors ${showCameraMenu ? 'bg-white text-black border-white' : 'bg-black/40 backdrop-blur-xl text-white border-white/20'}`} data-id="element-2075">
          <Camera className="w-6 h-6" data-id="element-2076" />
          <div className="absolute -top-1 -right-1 w-5 h-5 bg-blue-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center border-2 border-black" data-id="element-2077">
            {activeCameraIndex}
          </div>
        </motion.button>
      </motion.div>

      {/* Floating AI Assistant Button */}
      <motion.button whileTap={{
      scale: 0.92
    }} onClick={() => setIsAssistantActive(!isAssistantActive)} animate={{
      bottom: sheetState === 'collapsed' ? 64 : sheetState === 'peek' ? 230 : 20
    }} transition={{
      type: 'spring',
      damping: 25,
      stiffness: 200
    }} className={`absolute right-4 z-30 w-14 h-14 rounded-full flex items-center justify-center shadow-2xl ${isAssistantActive ? 'bg-blue-500 text-white' : 'bg-white text-black'}`} data-id="element-2078">
        {isAssistantActive ? <Mic className="w-6 h-6 animate-pulse" data-id="element-2079" /> : <Sparkles className="w-6 h-6" data-id="element-2080" />}
        {isAssistantActive && <span className="absolute inset-0 rounded-full border-2 border-blue-400 animate-ping opacity-75" data-id="element-2081" />}
      </motion.button>

      {/* Bottom Sheet */}
      <MobileOptionsSheet activeItem={activeItem} sheetState={sheetState} onSheetStateChange={setSheetState} onActiveItemChange={handleActiveItemChange} isStepMode currentStepIndex={currentStepIndex} totalSteps={flatSteps.length} currentStepCategory={currentStep?.category} currentStepItemName={currentStep?.item.name} onNextStep={handleNextStep} onPrevStep={handlePrevStep} data-id="element-2082" />
    </div>;
}